import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { ChartTooltip } from "@/components/ui/chart-tooltip";
import { PeopleHistogramModal } from "./PeopleHistogramModal";
import { X, Minus, ChevronDown, Users } from "lucide-react";
import { getObraStats, workforceSeries } from "@/services/obraStats";

interface AttendanceChartProps {
  obraId?: string;
  onClose?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
}

export function AttendanceChart({ obraId, onClose, onMinimize, isMinimized }: AttendanceChartProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Série real: pessoas/dia derivadas do workforce dos RDOs da obra
  const [attendanceData, setAttendanceData] = useState<Array<{ day: string; count: number }>>([]);
  useEffect(() => {
    let active = true;
    if (!obraId) { setAttendanceData([]); return; }
    getObraStats(obraId)
      .then((s) => {
        if (!active) return;
        const series = workforceSeries(s.rdos).slice(-10).map((p) => {
          const [, m, d] = p.date.split("-");
          return { day: `${d}/${m}`, count: p.pessoas };
        });
        setAttendanceData(series);
      })
      .catch((e) => console.error("AttendanceChart:", e));
    return () => { active = false; };
  }, [obraId]);

  return (
    <>
      <Card className="shadow-card transition-shadow">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle 
              className="text-lg font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
              onClick={() => setIsModalOpen(true)}
            >
              Histograma de pessoas
              <span className="text-xs font-normal text-muted-foreground ml-2">Ult.: 10 dias</span>
            </CardTitle>
            <div className="flex items-center gap-1">
              {onMinimize && (
                <button
                  onClick={onMinimize}
                  className="inline-flex h-11 w-11 items-center justify-center rounded hover:bg-muted transition-colors sm:h-8 sm:w-8"
                  title={isMinimized ? "Maximizar" : "Minimizar"}
                >
                  {isMinimized ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="inline-flex h-11 w-11 items-center justify-center rounded hover:bg-muted transition-colors sm:h-8 sm:w-8"
                  title="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        {!isMinimized && (
          <CardContent>
            {attendanceData.length === 0 ? (
              <div className="h-64 flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
                <Users className="h-8 w-8 opacity-40" />
                <span>Sem dados de mão de obra ainda. Preencha RDOs para ver o histograma.</span>
              </div>
            ) : (
            <div className="h-64 cursor-pointer" onClick={() => setIsModalOpen(true)}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={attendanceData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.3} />
                  <XAxis 
                    dataKey="day" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, fill: "hsl(var(--primary))" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            )}
          </CardContent>
        )}
    </Card>
    
    <PeopleHistogramModal
      open={isModalOpen}
      onOpenChange={setIsModalOpen}
      obraId={obraId}
    />
    </>
  );
}